import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { updateGiftTableState } from "../../../../redux/slices/giftTableSlice";
import { RootState } from "../../../../redux/store";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import { registrationSteps } from "../../constants/steps";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { Alert } from "@repo/ui";
import { gsap } from "gsap";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import "./ChooseTheme.css";
import { themeLocale } from "../../../../constants/themeLocale";
import NavigationButtons from "../../components/NavigationButtons";

import TropicalImg from "../../../../assets/themes/stylish-summer-holiday-illustration-watercolor-painting-palm-trees-beach-with-ocean-ideal-logos-tshirts-isolated-white-min.jpg";
import FairyTailImg from "../../../../assets/themes/conifer-forest-with-path-watercolor-clipart-style-min.jpg";
import HaciendaImg from "../../../../assets/themes/summer-landscape-oil-painting-charming-house-by-serene-river-with-vibrant-autumn-foliage-min.jpg";
import FlowerGardenImg from "../../../../assets/images/watercolor/fall20.jpg";
import MagicalNightImg from "../../../../assets/images/watercolor/watercolor-paint-glass-champagne-with-bubbles-holiday-card-decor.jpg";

const LOCALE = "modules_Registration_pages_ChooseTheme_ChooseTheme";

const themes = [
  { id: "tropical", img: TropicalImg },
  { id: "fairy_tale", img: FairyTailImg },
  { id: "hacienda", img: HaciendaImg },
  { id: "flower_garden", img: FlowerGardenImg },
  { id: "magical_night", img: MagicalNightImg },
];

const ChooseTheme: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const giftTable = useSelector((state: RootState) => state.giftTable);
  const [selectedTheme, setSelectedTheme] = useState<string | null>(
    giftTable.theme || null
  );
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  const handleThemeSelect = (themeId: string) => {
    setSelectedTheme(themeId);
    // loadTheme(themeId);
    localStorage.setItem("theme", themeId);
  };

  const handleContinue = () => {
    if (!selectedTheme) {
      setAlert({ message: t("selectThemeError"), level: "warn" });
      return;
    }

    dispatch(updateGiftTableState({ theme: selectedTheme }));
    navigate("/registration/create-gift-table/summary");
  };

  const handleBack = () => {
    navigate("/registration/create-gift-table/step4");
  };

  return (
    <PageContainer>
      <StepIndicator
        steps={registrationSteps}
        currentStep={5}
        progressPercentage={80}
      />
      <AnimatedCard title={t("title")} className="bg-white max-w-lg w-full">
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}

        <div className="grid grid-areas gap-4 md:grid-cols-3">
          {themes.map(({ id, img }) => (
            <div
              key={id}
              className={`theme-container ${
                selectedTheme === id
                  ? "border-2 border-primary rounded-lg"
                  : "border-2 border-transparent"
              } hover:shadow-lg transition-all`}
              onClick={() => handleThemeSelect(id)}
            >
              <img
                src={img}
                alt={t(themeLocale[id])}
                className="w-full h-full object-cover rounded-lg"
              />
              <span className="theme-name">{t(themeLocale[id])}</span>
            </div>
          ))}
        </div>

        <NavigationButtons
          continueLabel={t("continueButton")}
          backLabel={t("backButton", { defaultValue: "Regresar" })}
          onContinue={handleContinue}
          onBack={handleBack}
          className="w-full mt-4"
        />
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(ChooseTheme));
